// CoverageBoard — who is covering which client, where the gaps are, and
// which open hours are offered to which contracted agency. Lives as the
// Agency Scheduling tab of Schedule (CAERA) and, until the scheduling module
// is switched on for everyone, also at /hh/staffing.
//
// Two things are deliberately kept apart here:
//
//   Coverage  — what the schedule says will happen this week, read from the
//               unified visit feed (AxisCare today, CAERA for any line
//               switched over). Shifts, hours, gaps.
//   Assigned  — the standing arrangement recorded in CAERA
//               (`client_staff_assignments`). Survives the week passing.
//
// They are not the same fact and they drift apart, which is exactly what the
// board is for: a nurse on the calendar with no recorded assignment, or a
// recorded assignment with no shifts, are both worth seeing.
import { useEffect, useState, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../hooks/useAuth';
import { schedulingGuard, SCHEDULING_GUARD_FIELDS } from '../../lib/staffCompliance';
import { getVisitsByDateRange, visitHours } from '../../lib/axiscare';
import { format, addDays, startOfWeek, parseISO } from 'date-fns';
import DataTable from '../../components/DataTable';
import { StatTile } from '../../components/StatTile';
import { petConflict } from '../../lib/pets';
import { visitsFromResponse } from '../../lib/axiscareVisits';
import { LIVE_OFFER_STATUSES, offerSummary, offerStatusText, extendOfferUpdate } from '../../lib/agencyOffers';
import OfferAgencyModal from './OfferAgencyModal';

const fmtH = (n) => `${Number(n || 0).toFixed(n % 1 === 0 ? 0 : 1)}h`;
const iso = (d) => format(d, 'yyyy-MM-dd');
const staffName = (s) => (s ? `${s.last_name || ''}, ${s.first_name || ''}`.replace(/^, |, $/, '').trim() : '');
const shortName = (s) => (s ? (s.last_name || s.first_name || '').trim() : 'Unassigned');

// Service codes map onto the two authorised hour buckets. The Service
// Catalog is the source (skill_kind per AxisCare code or alias); the two
// original AxisCare codes stay as a fallback.
const SERVICE_KIND = { 'RN - Skilled': 'skilled', 'PCS/FCO': 'pss', 'PCS/CNA': 'pss' };
const serviceKind = (code, kinds = {}) => kinds[code] || SERVICE_KIND[code] || 'skilled';

// One visit from the unified feed, in the shape the board reads.
const toBoardVisit = (v, clientByAx, staffById, staffByAx) => {
  const st = v.caregiver ? (v.caregiver.staffId ? staffById[v.caregiver.staffId] : staffByAx[String(v.caregiver.id)]) || null : null;
  return {
    id: String(v.visitId || v.id),
    client_id: v.client?.caeraId || clientByAx[String(v.client?.id)] || null,
    staff_id: st?.id || null,
    caregiver_axiscare_id: v.caregiver ? String(v.caregiver.id) : null,
    caregiver_name: v.caregiver ? `${v.caregiver.lastName || ''}, ${v.caregiver.firstName || ''}`.replace(/^, |, $/, '').trim() : '',
    scheduled_start: v.scheduledStartDate || v.startDate,
    scheduled_end: v.scheduledEndDate || v.endDate,
    service_code: v.service?.code || '',
    units: visitHours(v),
    removed: !!v.removed,
    staff: st,
  };
};

const hoursOf = (v) => {
  if (v.units != null) return Number(v.units);
  if (!v.scheduled_start || !v.scheduled_end) return 0;
  const h = (new Date(v.scheduled_end) - new Date(v.scheduled_start)) / 3_600_000;
  return h > 0 ? Math.round(h * 100) / 100 : 0;
};

const Chip = ({ children, tone = 'neutral', title, dashed }) => {
  const t = {
    neutral: { bg: 'var(--bg2)', fg: 'var(--text2)', bd: 'var(--border)' },
    agency:  { bg: 'var(--info-bg)', fg: 'var(--info)', bd: 'var(--info-border)' },
    open:    { bg: 'var(--danger-bg)', fg: 'var(--danger)', bd: 'var(--danger-border)' },
    ok:      { bg: 'var(--success-bg)', fg: 'var(--success)', bd: 'var(--success-border)' },
    warn:    { bg: 'var(--warn-bg)', fg: 'var(--warn)', bd: 'var(--warn-border)' },
  }[tone];
  return (
    <span
      title={title}
      style={{
        display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11,
        padding: '2px 7px', borderRadius: 4, whiteSpace: 'nowrap', margin: '1px 3px 1px 0',
        background: t.bg, color: t.fg, border: `0.5px ${dashed ? 'dashed' : 'solid'} ${t.bd}`,
      }}
    >
      {children}
    </span>
  );
};

export default function CoverageBoard() {
  const navigate = useNavigate();
  const { profile } = useAuth();

  // Sunday-start, matching AxisCare's own calendar and the payroll runs
  // (payroll_runs rows are all Sun→Sat). A board that disagreed with the
  // schedule it mirrors would put shifts in the wrong week at the boundary.
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date(), { weekStartsOn: 0 }));
  const [clients, setClients] = useState([]);
  const [visits, setVisits] = useState([]);
  const [assignments, setAssignments] = useState([]);
  const [agencies, setAgencies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kinds, setKinds] = useState({});
  const [error, setError] = useState('');
  const [toast, setToast] = useState('');
  const [tab, setTab] = useState('coverage');   // 'coverage' | 'week'
  const [scope, setScope] = useState('gaps');   // 'gaps' | 'all' | agency uuid
  const [expanded, setExpanded] = useState(null);
  const [busyPair, setBusyPair] = useState(null);
  const [offers, setOffers] = useState([]);
  const [tokens, setTokens] = useState([]);
  const [accessLog, setAccessLog] = useState([]);
  const [showShare, setShowShare] = useState(false);
  const [shareBusy, setShareBusy] = useState(null);
  const [copied, setCopied] = useState(null);

  const weekDays = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const weekEnd = weekDays[6];

  const showToast = (msg) => { setToast(msg); setTimeout(() => setToast(''), 4000); };

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    const staffSelect = `id, first_name, last_name, position_code, employment_type, contracting_agency_id, pet_restrictions, ${SCHEDULING_GUARD_FIELDS}, contracting_agency:contracting_agencies(id, name)`;

    const [cRes, vRes, sRes, katRes, aRes, agRes, oRes, tRes] = await Promise.all([
      supabase.from('clients')
        .select('id, client_id, first_name, last_name, county, current_status, client_status_zone, pa_skilled_hours_week, pa_pss_hours_week, pa_expiry_date, axiscare_client_id, household_pets, household_pets_other, household_pets_notes')
        .eq('is_test', false)
        .in('client_status_zone', ['Active', 'Authorization'])
        .order('last_name'),
      getVisitsByDateRange(iso(weekStart), iso(weekEnd)),
      supabase.from('staff').select(`${staffSelect}, axiscare_id`),
      supabase.from('service_catalog').select('axiscare_service_code, axiscare_service_aliases, skill_kind'),
      supabase.from('client_staff_assignments')
        .select(`id, client_id, staff_id, role_code, service_type, hours_per_week, status, start_date, contracting_agency_id, staff:staff_id(${staffSelect})`)
        .in('status', ['proposed', 'active']),
      supabase.from('contracting_agencies').select('id, name, chat_webhook_url').order('name'),
      supabase.from('client_agency_offers')
        .select('id, client_id, contracting_agency_id, status, service_type, role_needed, hours_needed, start_date, note, expires_at, responded_by_name, response_note, created_at, agency:contracting_agencies(id, name)')
        .in('status', LIVE_OFFER_STATUSES),
      supabase.from('agency_board_tokens')
        .select('id, contracting_agency_id, token, label, expires_at, last_accessed_at, access_count, created_at, created_by_name, agency:contracting_agencies(id, name)')
        .is('revoked_at', null),
    ]);

    const firstErr = [cRes, vRes, sRes, katRes, aRes, agRes, oRes, tRes].find(r => r.error)?.error;
    if (firstErr) setError(`Could not load the board: ${firstErr.message}`);

    const clientByAx = {}; (cRes.data || []).forEach(c => { if (c.axiscare_client_id != null) clientByAx[String(c.axiscare_client_id)] = c.id; });
    const staffById = {}; const staffByAx = {};
    (sRes.data || []).forEach(st => { staffById[st.id] = st; if (st.axiscare_id) staffByAx[String(st.axiscare_id)] = st; });
    const kindMap = {};
    (katRes.data || []).forEach(l => {
      const k = l.skill_kind === 'skilled' ? 'skilled' : 'pss';
      if (l.axiscare_service_code) kindMap[l.axiscare_service_code] = k;
      (l.axiscare_service_aliases || []).forEach(a => { kindMap[a] = k; });
    });
    setKinds(kindMap);
    const raw = vRes.data?.results?.visits;
    const feed = visitsFromResponse(raw);
    setClients(cRes.data || []);
    setVisits(feed.map(v => toBoardVisit(v, clientByAx, staffById, staffByAx)));
    setAssignments(aRes.data || []);
    setAgencies(agRes.data || []);
    setOffers(oRes.data || []);
    setTokens(tRes.data || []);
    setLoading(false);
  }, [weekStart, weekEnd]);

  useEffect(() => { load(); }, [load]);

  // ── Rows: coverage + recorded assignment, per client ──────────────────────
  const rows = useMemo(() => {
    const byClient = {};
    for (const v of visits) {
      if (v.removed || !v.client_id) continue;
      const b = (byClient[v.client_id] = byClient[v.client_id] || { staffed: 0, open: 0, byStaff: {}, days: {}, openBy: {} });
      const h = hoursOf(v);
      const day = v.scheduled_start.slice(0, 10);
      (b.days[day] = b.days[day] || []).push(v);
      if (v.caregiver_axiscare_id) {
        b.staffed += h;
        const key = v.staff_id || `ax:${v.caregiver_axiscare_id}`;
        b.byStaff[key] = b.byStaff[key] || {
          key, staffId: v.staff_id, staff: v.staff, name: v.staff ? staffName(v.staff) : v.caregiver_name,
          hasFile: !!v.staff_id, hours: 0, kinds: new Set(),
        };
        b.byStaff[key].hours += h;
        b.byStaff[key].kinds.add(serviceKind(v.service_code, kinds));
      } else {
        b.open += h;
        const k = serviceKind(v.service_code, kinds);
        b.openBy[k] = (b.openBy[k] || 0) + h;
      }
    }

    const byClientAssign = assignments.reduce((acc, a) => {
      (acc[a.client_id] = acc[a.client_id] || []).push(a);
      return acc;
    }, {});
    const byClientOffer = offers.reduce((acc, o) => {
      (acc[o.client_id] = acc[o.client_id] || []).push(o);
      return acc;
    }, {});

    return clients.map(c => {
      const b = byClient[c.id] || { staffed: 0, open: 0, byStaff: {}, days: {}, openBy: {} };
      const sk = c.pa_skilled_hours_week != null ? Number(c.pa_skilled_hours_week) : null;
      const ps = c.pa_pss_hours_week != null ? Number(c.pa_pss_hours_week) : null;
      const auth = sk == null && ps == null ? null : (sk || 0) + (ps || 0);
      const covering = Object.values(b.byStaff).sort((x, y) => y.hours - x.hours);
      const recorded = byClientAssign[c.id] || [];
      const recordedStaffIds = new Set(recorded.map(a => a.staff_id));

      return {
        ...c,
        name: `${c.last_name}, ${c.first_name}`,
        authSkilled: sk, authPss: ps, auth,
        staffed: Math.round(b.staffed * 10) / 10,
        open: Math.round(b.open * 10) / 10,
        openBy: b.openBy,
        covering,
        days: b.days,
        recorded,
        offers: byClientOffer[c.id] || [],
        // A nurse working shifts with no recorded assignment — the board's
        // main prompt to write the arrangement down.
        unrecorded: covering.filter(cg => cg.staffId && !recordedStaffIds.has(cg.staffId)),
        agencyIds: [...new Set(covering.map(cg => cg.staff?.contracting_agency_id).filter(Boolean))],
        hasOrphan: covering.some(cg => !cg.hasFile),
      };
    });
  }, [clients, visits, assignments, offers, kinds]);

  const filtered = useMemo(() => {
    const base = scope === 'gaps'
      ? rows.filter(r => r.open > 0 || (r.client_status_zone === 'Active' && r.staffed === 0))
      : scope === 'all'
        ? rows
        : rows.filter(r => r.agencyIds.includes(scope));
    // Biggest gap first — the board is a work queue, not an alphabetical list.
    return [...base].sort((a, b) => (b.open - a.open) || (a.staffed - b.staffed) || a.name.localeCompare(b.name));
  }, [rows, scope]);

  const stats = useMemo(() => {
    const active = rows.filter(r => r.client_status_zone === 'Active');
    const staffed = rows.reduce((s, r) => s + r.staffed, 0);
    const open = rows.reduce((s, r) => s + r.open, 0);
    const gaps = rows.filter(r => r.open > 0).length;
    const uncovered = active.filter(r => r.staffed === 0).length;
    const agencyHours = rows.reduce((s, r) =>
      s + r.covering.filter(cg => cg.staff?.contracting_agency_id).reduce((t, cg) => t + cg.hours, 0), 0);
    return { active: active.length, staffed, open, gaps, uncovered, agencyHours };
  }, [rows]);

  // ── Record / end an assignment ────────────────────────────────────────────
  const recordAssignment = async (client, cg) => {
    // Policy A-2.1: flagged, or GCHEXS pending past the provisional window,
    // cannot be recorded as assigned; other open items are confirmed past.
    const guard = schedulingGuard(cg.staff);
    if (guard?.level === 'block') { setError(`${cg.name}: ${guard.label} — ${guard.detail}`); return; }
    if (guard && !window.confirm(`${cg.name}: ${guard.label}\n\n${guard.detail}\n\nRecord the assignment anyway?`)) return;
    const pets = petConflict({ ...cg.staff, full_name: cg.name }, client);
    if (pets && !window.confirm(`Household pets\n\n${pets}\n\nRecord the assignment anyway?`)) return;
    setBusyPair(`${client.id}:${cg.key}`);
    const kinds = [...cg.kinds];
    const { error: err } = await supabase.from('client_staff_assignments').insert([{
      client_id: client.id,
      staff_id: cg.staffId,
      role_code: cg.staff?.position_code || null,
      contracting_agency_id: cg.staff?.contracting_agency_id || null,
      service_type: kinds.length === 1 ? kinds[0] : 'mixed',
      hours_per_week: Math.round(cg.hours * 10) / 10,
      status: 'active',
      start_date: iso(new Date()),
      created_by: profile?.id || null,
      created_by_name: profile?.full_name || null,
    }]);
    setBusyPair(null);
    if (err) { setError(`Could not record the assignment: ${err.message}`); return; }
    showToast(`${cg.name} recorded as assigned to ${client.name}.`);
    load();
  };

  const endAssignment = async (a, client) => {
    if (!window.confirm(`End ${staffName(a.staff)}'s assignment to ${client.name}? Their scheduled shifts aren't changed.`)) return;
    setBusyPair(`end:${a.id}`);
    const { error: err } = await supabase.from('client_staff_assignments')
      .update({ status: 'ended', end_date: iso(new Date()), updated_at: new Date().toISOString() })
      .eq('id', a.id);
    setBusyPair(null);
    if (err) { setError(`Could not end the assignment: ${err.message}`); return; }
    showToast(`Assignment ended.`);
    load();
  };

  // ── Offer a client's open hours to an agency ──────────────────────────────
  // An agency sees a client only where they already staff them or where this
  // was deliberately clicked. There is no county/skill auto-match: exposing a
  // child's details to an outside agency should be a decision, not a rule.
  // The offer form (OfferAgencyModal) asks what is needed and posts it; the
  // agency is told in its Chat space and answers on its board.
  const [offering, setOffering] = useState(null);   // { client, agency, defaults }
  const offerToAgency = (client, agencyId) => {
    const agency = agencies.find(a => a.id === agencyId);
    if (!agency) return;
    const kinds = Object.keys(client.openBy);
    setOffering({
      client: { id: client.id, name: client.name }, agency: { id: agency.id, name: agency.name },
      defaults: { hours_needed: client.open || '', role_needed: kinds.length === 1 && kinds[0] === 'pss' ? 'pca' : '' },
    });
  };

  const extendOffer = async (offer, client) => {
    setBusyPair(`extend:${offer.id}`);
    const { error: err } = await supabase.from('client_agency_offers')
      .update(extendOfferUpdate(offer)).eq('id', offer.id).eq('status', 'open');
    setBusyPair(null);
    if (err) { setError(`Could not extend the offer: ${err.message}`); return; }
    showToast(`${client.name}'s offer to ${offer.agency?.name || 'the agency'} extended by 7 days.`);
    load();
  };

  const withdrawOffer = async (offer, client) => {
    setBusyPair(`unoffer:${offer.id}`);
    const { error: err } = await supabase.from('client_agency_offers')
      .update({ status: 'withdrawn', closed_at: new Date().toISOString(), closed_reason: `Withdrawn by ${profile?.full_name || 'the office'}`, updated_at: new Date().toISOString() })
      .eq('id', offer.id);
    setBusyPair(null);
    if (err) { setError(`Could not withdraw the offer: ${err.message}`); return; }
    showToast(`${client.name} withdrawn from ${offer.agency?.name || 'the agency'}.`);
    load();
  };

  // ── Share links ───────────────────────────────────────────────────────────
  const boardUrl = (t) => `${window.location.origin}/agency/${t.token}`;

  // A link is the only key to an agency's board and the children's care
  // plans (no sign-in), and it sits in a Google Chat space whose history
  // outlives it, so it lasts LINK_DAYS and is then reviewed. Extending keeps
  // the same address, so the link pinned in the agency's space keeps working;
  // only "Turn off" and a new link change it.
  const LINK_DAYS = 90;
  const isExpired = (t) => !!t.expires_at && new Date(t.expires_at) < new Date();

  const createToken = async (agency) => {
    setShareBusy(agency.id);
    const token = `${crypto.randomUUID()}${crypto.randomUUID()}`.replace(/-/g, '');
    const expires = new Date(); expires.setDate(expires.getDate() + LINK_DAYS);
    const { error: err } = await supabase.from('agency_board_tokens').insert([{
      contracting_agency_id: agency.id,
      token,
      label: `${agency.name} staffing board`,
      expires_at: expires.toISOString(),
      created_by: profile?.id || null,
      created_by_name: profile?.full_name || null,
    }]);
    setShareBusy(null);
    if (err) { setError(`Could not create the link: ${err.message}`); return; }
    showToast(`Link created for ${agency.name}. It expires in ${LINK_DAYS} days.`);
    load();
  };

  // 90 more days from today, or from the current expiry if that is later.
  const extendToken = async (t) => {
    setShareBusy(t.id);
    const from = t.expires_at && new Date(t.expires_at) > new Date() ? new Date(t.expires_at) : new Date();
    from.setDate(from.getDate() + LINK_DAYS);
    const { error: err } = await supabase.from('agency_board_tokens')
      .update({ expires_at: from.toISOString() }).eq('id', t.id);
    setShareBusy(null);
    if (err) { setError(`Could not extend the link: ${err.message}`); return; }
    showToast(`${t.agency?.name || 'The agency'}'s link now expires ${format(from, 'd MMM yyyy')}. The address is unchanged.`);
    load();
  };

  const revokeToken = async (t) => {
    if (!window.confirm(`Turn off ${t.agency?.name || 'this agency'}'s link? Anyone holding it loses access immediately.`)) return;
    setShareBusy(t.id);
    const { error: err } = await supabase.from('agency_board_tokens')
      .update({ revoked_at: new Date().toISOString() }).eq('id', t.id);
    setShareBusy(null);
    if (err) { setError(`Could not revoke the link: ${err.message}`); return; }
    showToast('Link turned off.');
    load();
  };

  const saveWebhook = async (agency, url) => {
    const clean = url.trim();
    if (clean && !clean.startsWith('https://chat.googleapis.com/')) {
      setError('That doesn’t look like a Google Chat webhook. It should start with https://chat.googleapis.com/');
      return;
    }
    const { error: err } = await supabase.from('contracting_agencies')
      .update({ chat_webhook_url: clean || null, updated_at: new Date().toISOString() })
      .eq('id', agency.id);
    if (err) { setError(`Could not save the webhook: ${err.message}`); return; }
    showToast(clean ? `Google Chat webhook saved for ${agency.name}.` : `Webhook cleared for ${agency.name}.`);
    load();
  };

  const openShare = async () => {
    setShowShare(true);
    const { data } = await supabase.from('agency_board_access_log')
      .select('id, outcome, accessed_at, contracting_agency_id, staff_viewer_name, agency:contracting_agencies(name)')
      .order('accessed_at', { ascending: false }).limit(15);
    setAccessLog(data || []);
  };

  const copyLink = async (t) => {
    try {
      await navigator.clipboard.writeText(boardUrl(t));
      setCopied(t.id);
      setTimeout(() => setCopied(null), 2500);
    } catch {
      setError('Could not copy — select the link and copy it manually.');
    }
  };

  // ── Coverage table ────────────────────────────────────────────────────────
  const columns = [
    {
      id: 'name', accessorKey: 'name', header: 'Client', size: 190,
      meta: { mobile: 'primary' },
      cell: info => {
        const r = info.row.original;
        return (
          <div>
            <div style={{ fontWeight: 500, color: 'var(--accent)', cursor: 'pointer' }} onClick={() => navigate(`/clients/${r.id}`)}>{r.name}</div>
            <div style={{ fontSize: 11, color: 'var(--text3)', fontFamily: 'monospace' }}>{r.client_id}</div>
          </div>
        );
      },
    },
    { accessorKey: 'county', header: 'County', size: 100, meta: { mobile: 'secondary' }, cell: i => <span style={{ fontSize: 12, color: 'var(--text3)' }}>{i.getValue() || '—'}</span> },
    {
      id: 'auth', accessorKey: 'auth', header: 'Authorised', size: 110, meta: { mobile: 'secondary', align: 'right' },
      cell: info => {
        const r = info.row.original;
        if (r.auth == null) return <span title="No PA hours recorded — authorised vs staffed can't be computed" style={{ fontSize: 11, color: 'var(--warn)' }}>not set</span>;
        return (
          <span style={{ fontFamily: 'monospace' }}>
            {fmtH(r.auth)}
            {r.authSkilled != null && r.authPss ? <span style={{ fontSize: 10, color: 'var(--text3)' }}> ({r.authSkilled}+{r.authPss})</span> : null}
          </span>
        );
      },
    },
    { id: 'staffed', accessorKey: 'staffed', header: 'Staffed', size: 90, meta: { mobile: 'primary', align: 'right', mono: true }, cell: i => <span style={{ color: i.getValue() > 0 ? 'var(--text)' : 'var(--text3)' }}>{fmtH(i.getValue())}</span> },
    {
      id: 'open', accessorKey: 'open', header: 'Open', size: 90, meta: { mobile: 'primary', align: 'right', mono: true },
      cell: info => {
        const v = info.getValue();
        return v > 0
          ? <span style={{ color: 'var(--danger)', fontWeight: 600 }}>{fmtH(v)}</span>
          : <span style={{ color: 'var(--text3)' }}>—</span>;
      },
    },
    {
      id: 'meter', header: 'Coverage', size: 110, enableSorting: false, meta: { mobile: 'hidden' },
      cell: info => {
        const r = info.row.original;
        const total = r.staffed + r.open;
        if (total === 0) return <span style={{ fontSize: 11, color: 'var(--text3)' }}>no shifts</span>;
        const pct = Math.round((r.staffed / total) * 100);
        return (
          <div title={`${pct}% of scheduled hours are covered`} style={{ display: 'flex', height: 7, width: 92, borderRadius: 3, overflow: 'hidden', border: '0.5px solid var(--border)', background: 'var(--bg2)' }}>
            <span style={{ width: `${pct}%`, background: 'var(--accent)' }} />
            <span style={{ width: `${100 - pct}%`, background: 'var(--danger)' }} />
          </div>
        );
      },
    },
    {
      id: 'covering', header: 'Covering this week', size: 260, enableSorting: false, meta: { mobile: 'secondary' },
      cell: info => {
        const r = info.row.original;
        if (!r.covering.length) {
          return r.open > 0
            ? <Chip tone="open">Nobody assigned</Chip>
            : <Chip tone="warn">No shifts on the calendar</Chip>;
        }
        return (
          <div>
            {r.covering.map(cg => (
              <Chip
                key={cg.key}
                tone={cg.staff?.contracting_agency_id ? 'agency' : 'neutral'}
                dashed={!cg.hasFile}
                title={!cg.hasFile
                  ? 'On the schedule with no CAERA personnel file'
                  : cg.staff?.contracting_agency?.name || 'Direct'}
              >
                {shortName(cg.staff) || cg.name}
                {(() => { const g = schedulingGuard(cg.staff); return g ? <span title={`${g.label} — ${g.detail}`} style={{ fontSize: 10, color: g.level === 'block' ? 'var(--danger)' : 'var(--warn)' }}>{g.level === 'block' ? '⛔' : '⚠'}</span> : null; })()}
                <span style={{ fontSize: 9, opacity: 0.8 }}>{cg.staff?.position_code || 'no file'}</span>
                <span style={{ fontFamily: 'monospace', fontSize: 10, opacity: 0.75 }}>{fmtH(cg.hours)}</span>
              </Chip>
            ))}
          </div>
        );
      },
    },
    {
      id: 'recorded', header: 'Assigned', size: 150, enableSorting: false, meta: { mobile: 'secondary' },
      cell: info => {
        const r = info.row.original;
        if (!r.recorded.length) {
          if (r.offers.length) return r.offers.map(o => <Chip key={o.id} tone="agency" title="Visible on this agency's shared board">offered · {o.agency?.name}</Chip>);
          return r.covering.some(c => c.staffId)
            ? <Chip tone="warn" title="Covered on the calendar, but no standing assignment recorded in CAERA">not recorded</Chip>
            : <span style={{ fontSize: 11, color: 'var(--text3)' }}>—</span>;
        }
        return (
          <div>
            {r.recorded.map(a => (
              <Chip key={a.id} tone="ok" title={`Recorded ${a.start_date || ''}`}>{shortName(a.staff)}</Chip>
            ))}
            {r.unrecorded.length > 0 && <Chip tone="warn" title="Working shifts with no recorded assignment">+{r.unrecorded.length} unrecorded</Chip>}
            {r.offers.map(o => <Chip key={o.id} tone="agency" title="Visible on this agency's shared board">offered · {o.agency?.name}</Chip>)}
          </div>
        );
      },
    },
    {
      id: 'pa', accessorKey: 'pa_expiry_date', header: 'PA ends', size: 100, meta: { mobile: 'hidden' },
      cell: info => {
        const d = info.getValue();
        if (!d) return <span style={{ fontSize: 11, color: 'var(--text3)' }}>—</span>;
        const expired = d < iso(new Date());
        return (
          <span style={{ fontSize: 11, fontFamily: 'monospace', color: expired ? 'var(--danger)' : 'var(--text3)', fontWeight: expired ? 600 : 400 }}>
            {format(parseISO(d), 'd MMM yy')}{expired ? ' ⚑' : ''}
          </span>
        );
      },
    },
    {
      id: 'expand', header: '', size: 90, enableSorting: false, meta: { mobile: 'secondary' },
      cell: info => {
        const r = info.row.original;
        const open = expanded === r.id;
        return (
          <button
            className="btn btn-outline btn-sm"
            style={{ fontSize: 11 }}
            onClick={e => { e.stopPropagation(); setExpanded(open ? null : r.id); }}
          >
            {open ? 'Close' : 'Details'}
          </button>
        );
      },
    },
  ];

  const renderExpanded = (r) => (
    <div style={{ padding: '12px 14px', display: 'grid', gap: 14, gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))' }}>
      <div>
        <div style={{ fontSize: 9, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text3)', marginBottom: 6 }}>Recorded assignments</div>
        {r.recorded.length === 0 && <div style={{ fontSize: 12, color: 'var(--text3)' }}>None recorded yet.</div>}
        {r.recorded.map(a => (
          <div key={a.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '0.5px solid var(--border)' }}>
            <span style={{ fontSize: 12.5, fontWeight: 500 }}>{staffName(a.staff)}</span>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>{a.role_code} · {a.hours_per_week != null ? fmtH(a.hours_per_week) + '/wk' : 'hours not set'}</span>
            {a.staff?.contracting_agency?.name && <Chip tone="agency">{a.staff.contracting_agency.name}</Chip>}
            <button className="btn btn-outline btn-sm" style={{ fontSize: 10, marginLeft: 'auto' }} disabled={busyPair === `end:${a.id}`} onClick={() => endAssignment(a, r)}>End</button>
          </div>
        ))}
      </div>

      <div>
        <div style={{ fontSize: 9, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text3)', marginBottom: 6 }}>On the calendar, not recorded</div>
        {r.unrecorded.length === 0 && <div style={{ fontSize: 12, color: 'var(--text3)' }}>Nothing outstanding.</div>}
        {r.unrecorded.map(cg => (
          <div key={cg.key} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '5px 0', borderBottom: '0.5px solid var(--border)' }}>
            <span style={{ fontSize: 12.5, fontWeight: 500 }}>{cg.name}</span>
            <span style={{ fontSize: 11, color: 'var(--text3)' }}>{cg.staff?.position_code} · {fmtH(cg.hours)} this week</span>
            <button
              className="btn btn-primary btn-sm"
              style={{ fontSize: 10, marginLeft: 'auto' }}
              disabled={busyPair === `${r.id}:${cg.key}`}
              onClick={() => recordAssignment(r, cg)}
            >
              {busyPair === `${r.id}:${cg.key}` ? 'Recording…' : 'Record assignment'}
            </button>
          </div>
        ))}
        {r.hasOrphan && (
          <div style={{ fontSize: 11, color: 'var(--warn)', marginTop: 8 }}>
            ⚠ Someone covering this client has no CAERA personnel file, so they can't be assigned until one exists.
          </div>
        )}
      </div>

      {(r.open > 0 || r.offers.length > 0) && (
        <div>
          <div style={{ fontSize: 9, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--danger)', marginBottom: 6 }}>Open hours to fill</div>
          {Object.entries(r.openBy).map(([kind, h]) => (
            <div key={kind} style={{ fontSize: 12.5, color: 'var(--text2)' }}>
              {kind === 'pss' ? 'Personal support' : 'Skilled nursing'} — <strong style={{ color: 'var(--danger)' }}>{fmtH(h)}</strong>
            </div>
          ))}

          <div style={{ marginTop: 10, fontSize: 9, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text3)', marginBottom: 6 }}>Offered to</div>
          {r.offers.map(o => (
            <div key={o.id} style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 0' }}>
              <Chip tone={o.status === 'accepted' ? 'ok' : 'agency'}>{o.agency?.name}</Chip>
              <span style={{ fontSize: 10.5, color: 'var(--text3)' }}>{offerSummary(o)} · {offerStatusText(o)}</span>
              {o.status === 'open' && <button className="btn btn-outline btn-sm" style={{ fontSize: 10, marginLeft: 'auto' }} disabled={busyPair === `extend:${o.id}`} onClick={() => extendOffer(o, r)}>Extend</button>}
              <button className="btn btn-outline btn-sm" style={{ fontSize: 10, marginLeft: o.status === 'open' ? 0 : 'auto' }} disabled={busyPair === `unoffer:${o.id}`} onClick={() => withdrawOffer(o, r)}>Withdraw</button>
            </div>
          ))}
          {agencies.filter(a => !r.offers.some(o => o.contracting_agency_id === a.id)).map(a => (
            <button
              key={a.id}
              className="btn btn-outline btn-sm"
              style={{ fontSize: 10, marginRight: 5, marginTop: 4 }}
              disabled={busyPair === `offer:${r.id}:${a.id}`}
              title={`Make this client visible on ${a.name}'s shared board`}
              onClick={() => offerToAgency(r, a.id)}
            >
              {`Offer to ${a.name}`}
            </button>
          ))}
        </div>
      )}
    </div>
  );

  // ── Week grid ─────────────────────────────────────────────────────────────
  const gridRows = filtered.filter(r => Object.keys(r.days).length > 0 || r.open > 0);

  return (
    <div>
      {/* Controls */}
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, flexWrap: 'wrap', marginBottom: 14 }}>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <button className="btn btn-outline btn-sm" onClick={() => setWeekStart(d => addDays(d, -7))}>← Prev</button>
          <span style={{ fontSize: 13, fontWeight: 500, minWidth: 190, textAlign: 'center' }}>
            {format(weekStart, 'd MMM')} – {format(weekEnd, 'd MMM yyyy')}
          </span>
          <button className="btn btn-outline btn-sm" onClick={() => setWeekStart(d => addDays(d, 7))}>Next →</button>
          <button className="btn btn-outline btn-sm" onClick={() => setWeekStart(startOfWeek(new Date(), { weekStartsOn: 0 }))}>This week</button>
        </div>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
          <select
            value={scope}
            onChange={e => setScope(e.target.value)}
            style={{ fontSize: 12, padding: '5px 10px', borderRadius: 8, border: '0.5px solid var(--border)', background: 'var(--bg2)', color: 'var(--text)', fontFamily: 'inherit' }}
          >
            <option value="gaps">Needs coverage</option>
            <option value="all">All active clients</option>
            {agencies.map(a => <option key={a.id} value={a.id}>Covered by {a.name}</option>)}
          </select>
          <button className="btn btn-outline btn-sm" onClick={load} title="Reload the schedule">↻</button>
          <button className="btn btn-outline btn-sm" onClick={openShare}>Share with agency…</button>
        </div>
      </div>

      {error && <div style={{ background: 'var(--danger-bg)', border: '0.5px solid var(--danger-border)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--danger)', marginBottom: 12 }}>{error}</div>}
      {toast && <div style={{ background: 'var(--success-bg)', border: '0.5px solid var(--success-border)', borderRadius: 8, padding: '10px 14px', fontSize: 12, color: 'var(--success)', marginBottom: 12 }}>{toast}</div>}

      {/* Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(150px, 1fr))', gap: 8, marginBottom: 14 }}>
        <StatTile label="Active clients" value={stats.active} sub="receiving service" />
        <StatTile label="Staffed" value={fmtH(stats.staffed)} sub={stats.agencyHours > 0 ? `${fmtH(stats.agencyHours)} agency-supplied` : 'this week'} />
        <StatTile label="Open hours" value={fmtH(stats.open)} tone={stats.open > 0 ? 'alert' : 'neutral'} sub={`${stats.gaps} client${stats.gaps === 1 ? '' : 's'} with gaps`} />
        <StatTile label="No coverage" value={stats.uncovered} tone={stats.uncovered > 0 ? 'warn' : 'neutral'} sub="active, nothing booked" />
      </div>

      <div className="card">
        <div className="card-header" style={{ flexWrap: 'wrap', gap: 8 }}>
          <div style={{ display: 'flex', gap: 6 }}>
            {[['coverage', 'By client'], ['week', 'Week grid']].map(([k, label]) => (
              <button
                key={k}
                onClick={() => setTab(k)}
                style={{ padding: '4px 12px', borderRadius: 20, fontSize: 12, fontWeight: 500, cursor: 'pointer', fontFamily: 'inherit', border: '0.5px solid', background: tab === k ? 'var(--accent)' : 'transparent', color: tab === k ? 'var(--white)' : 'var(--text3)', borderColor: tab === k ? 'var(--accent)' : 'var(--border)' }}
              >
                {label}
              </button>
            ))}
          </div>
          <span style={{ marginLeft: 'auto', fontSize: 11, color: 'var(--text3)' }}>
            {filtered.length} client{filtered.length === 1 ? '' : 's'} shown
          </span>
        </div>

        {tab === 'coverage' ? (
          <DataTable
            tableId="staffing-board"
            columns={columns}
            data={filtered}
            getRowId={r => r.id}
            loading={loading}
            emptyMessage={scope === 'gaps' ? 'No coverage gaps this week.' : 'No clients match.'}
            isRowExpanded={r => expanded === r.id}
            renderExpandedRow={renderExpanded}
            maxHeight="calc(100vh - 380px)"
          />
        ) : loading ? (
          <div className="empty-state"><div className="loading-spinner" style={{ margin: '0 auto' }} /></div>
        ) : gridRows.length === 0 ? (
          <div className="empty-state" style={{ padding: '32px 0' }}>No shifts on the calendar this week.</div>
        ) : (
          <div className="table-wrapper" style={{ overflowX: 'auto' }}>
            <table style={{ minWidth: 940 }}>
              <thead>
                <tr>
                  <th style={{ color: 'var(--text3)', minWidth: 160 }}>Client</th>
                  {weekDays.map(d => (
                    <th key={d.toISOString()} style={{ color: 'var(--text3)', textAlign: 'center', minWidth: 108 }}>
                      {format(d, 'EEE')}<div style={{ fontSize: 13, color: 'var(--text2)', fontWeight: 500 }}>{format(d, 'd')}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {gridRows.map(r => (
                  <tr key={r.id}>
                    <td>
                      <div style={{ fontSize: 12.5, fontWeight: 500 }}>{r.name}</div>
                      <div style={{ fontSize: 10.5, color: r.open > 0 ? 'var(--danger)' : 'var(--text3)', fontFamily: 'monospace' }}>
                        {r.client_id}{r.open > 0 ? ` · ${fmtH(r.open)} open` : ''}
                      </div>
                    </td>
                    {weekDays.map(d => {
                      const cell = (r.days[iso(d)] || []).filter(v => !v.removed);
                      if (!cell.length) return <td key={d.toISOString()} style={{ textAlign: 'center', color: 'var(--text3)', fontSize: 11 }}>—</td>;
                      return (
                        <td key={d.toISOString()} style={{ padding: 4 }}>
                          {cell.map(v => {
                            const unfilled = !v.caregiver_axiscare_id;
                            const agency = !!v.staff?.contracting_agency_id;
                            return (
                              <div
                                key={v.id}
                                title={`${v.service_code || ''} · ${format(new Date(v.scheduled_start), 'HH:mm')}–${v.scheduled_end ? format(new Date(v.scheduled_end), 'HH:mm') : '?'}`}
                                style={{
                                  borderRadius: 4, padding: '4px 6px', marginBottom: 3, fontSize: 10.5, lineHeight: 1.35,
                                  background: unfilled ? 'var(--danger-bg)' : agency ? 'var(--info-bg)' : 'var(--accent-bg)',
                                  border: `0.5px ${unfilled ? 'dashed var(--danger-border)' : `solid ${agency ? 'var(--info-border)' : 'var(--accent-border)'}`}`,
                                  color: unfilled ? 'var(--danger)' : agency ? 'var(--info)' : 'var(--text2)',
                                }}
                              >
                                <div style={{ fontWeight: 600, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                  {unfilled ? 'OPEN' : shortName(v.staff) || v.caregiver_name}
                                </div>
                                <div style={{ fontFamily: 'monospace', opacity: 0.8 }}>
                                  {fmtH(hoursOf(v))} · {serviceKind(v.service_code, kinds) === 'pss' ? 'PSS' : v.staff?.position_code || 'RN'}
                                </div>
                              </div>
                            );
                          })}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        <div style={{ borderTop: '0.5px solid var(--border)', padding: '9px 16px', display: 'flex', gap: 16, flexWrap: 'wrap', fontSize: 11, color: 'var(--text3)' }}>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--accent-bg)', border: '0.5px solid var(--accent-border)', marginRight: 5, verticalAlign: -1 }} />AHFC staff</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--info-bg)', border: '0.5px solid var(--info-border)', marginRight: 5, verticalAlign: -1 }} />Agency-supplied</span>
          <span><span style={{ display: 'inline-block', width: 10, height: 10, borderRadius: 2, background: 'var(--danger-bg)', border: '0.5px dashed var(--danger-border)', marginRight: 5, verticalAlign: -1 }} />Open / unassigned</span>
          <span style={{ marginLeft: 'auto' }}>Live from the schedule feed · AxisCare and CAERA lines</span>
        </div>
      </div>

      {offering && (
        <OfferAgencyModal {...offering} profile={profile}
          onClose={() => setOffering(null)}
          onDone={() => { showToast(`${offering.client.name} offered to ${offering.agency.name}. They have been told in their Chat space.`); setOffering(null); load(); }} />
      )}

      {/* ── Share with an agency ── */}
      {showShare && (
        <div className="modal-overlay" onMouseDown={e => e.target === e.currentTarget && setShowShare(false)}>
          <div className="modal" onMouseDown={e => e.stopPropagation()} style={{ maxWidth: 660 }}>
            <div className="modal-header">
              <span>Share the board with an agency</span>
              <button className="btn btn-outline btn-sm" onClick={() => setShowShare(false)}>Close</button>
            </div>
            <div className="modal-body">
              <p style={{ fontSize: 12.5, color: 'var(--text3)', margin: '0 0 14px', lineHeight: 1.55, maxWidth: '68ch' }}>
                Each agency gets its own read-only link, showing only the clients they staff or have
                been offered. Pin it in their Google Chat space. Every open is logged, and a link can
                be turned off at any time.
              </p>

              {agencies.map(a => {
                const t = tokens.find(x => x.contracting_agency_id === a.id);
                return (
                  <div key={a.id} style={{ border: '0.5px solid var(--border)', borderRadius: 8, padding: '12px 14px', marginBottom: 10 }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
                      <span style={{ fontWeight: 500, fontSize: 13.5 }}>{a.name}</span>
                      {!t ? <Chip tone="warn">No link</Chip>
                        : isExpired(t) ? <Chip tone="open">Expired</Chip>
                        : <Chip tone="ok">Link active</Chip>}
                      <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                        {t ? (
                          <>
                            <button className="btn btn-outline btn-sm" style={{ fontSize: 11 }} onClick={() => copyLink(t)}>
                              {copied === t.id ? '✓ Copied' : 'Copy link'}
                            </button>
                            <button className={isExpired(t) ? 'btn btn-primary btn-sm' : 'btn btn-outline btn-sm'} style={{ fontSize: 11 }} disabled={shareBusy === t.id} onClick={() => extendToken(t)}
                              title={`Keep the same link working for ${LINK_DAYS} more days`}>
                              {shareBusy === t.id ? 'Extending…' : `Extend ${LINK_DAYS} days`}
                            </button>
                            <button className="btn btn-outline btn-sm" style={{ fontSize: 11, color: 'var(--danger)', borderColor: 'var(--danger-border)' }} disabled={shareBusy === t.id} onClick={() => revokeToken(t)}>
                              Turn off
                            </button>
                          </>
                        ) : (
                          <button className="btn btn-primary btn-sm" style={{ fontSize: 11 }} disabled={shareBusy === a.id} onClick={() => createToken(a)}>
                            {shareBusy === a.id ? 'Creating…' : 'Create link'}
                          </button>
                        )}
                      </div>
                    </div>
                    {t && (
                      <>
                        <div style={{ fontFamily: 'monospace', fontSize: 11, color: 'var(--text3)', marginTop: 8, wordBreak: 'break-all', background: 'var(--bg2)', padding: '6px 8px', borderRadius: 5 }}>
                          {boardUrl(t)}
                        </div>
                        <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
                          {!t.expires_at ? 'No expiry'
                            : isExpired(t)
                              ? <span style={{ color: 'var(--danger)', fontWeight: 600 }}>Expired {format(parseISO(t.expires_at), 'd MMM yyyy')}: the board, care plans and weekly Chat update are off until it is extended</span>
                              : `Expires ${format(parseISO(t.expires_at), 'd MMM yyyy')}`}
                          {' · '}
                          {t.access_count > 0
                            ? `opened ${t.access_count}×${t.last_accessed_at ? `, last ${format(parseISO(t.last_accessed_at), 'd MMM HH:mm')}` : ''}`
                            : 'never opened'}
                          {t.created_by_name ? ` · created by ${t.created_by_name}` : ''}
                        </div>
                      </>
                    )}
                    <div style={{ marginTop: 10 }}>
                      <label style={{ fontSize: 10, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.06em', color: 'var(--text3)', display: 'block', marginBottom: 4 }}>
                        Google Chat webhook
                      </label>
                      <input
                        className="form-input"
                        style={{ fontSize: 11.5, fontFamily: 'monospace' }}
                        defaultValue={a.chat_webhook_url || ''}
                        placeholder="https://chat.googleapis.com/v1/spaces/…"
                        onBlur={e => { if (e.target.value.trim() !== (a.chat_webhook_url || '')) saveWebhook(a, e.target.value); }}
                      />
                      <div style={{ fontSize: 10.5, color: 'var(--text3)', marginTop: 4 }}>
                        In the Chat space: <em>Apps &amp; integrations → Webhooks → Add webhook</em>, then paste the URL here.
                        A Friday-morning summary with the board link is posted automatically, so they can staff the coming week. Counts only — no client details.
                      </div>
                    </div>
                  </div>
                );
              })}

              {agencies.length === 0 && (
                <div style={{ fontSize: 12.5, color: 'var(--text3)' }}>
                  No contracting agencies on file. Add one under Training &amp; Compliance → Contracting Agencies.
                </div>
              )}

              <div style={{ marginTop: 16, fontSize: 9, fontWeight: 500, textTransform: 'uppercase', letterSpacing: '0.07em', color: 'var(--text3)', marginBottom: 6 }}>Recent access</div>
              {accessLog.length === 0 && <div style={{ fontSize: 12, color: 'var(--text3)' }}>No opens recorded yet.</div>}
              {accessLog.map(l => (
                <div key={l.id} style={{ display: 'flex', gap: 10, fontSize: 11.5, color: 'var(--text3)', padding: '3px 0', borderBottom: '0.5px solid var(--border)' }}>
                  <span style={{ fontFamily: 'monospace' }}>{format(parseISO(l.accessed_at), 'd MMM HH:mm')}</span>
                  <span>{l.agency?.name || 'unknown link'}{l.staff_viewer_name ? ` · viewed by ${l.staff_viewer_name} (AHFC)` : ''}</span>
                  <span style={{ marginLeft: 'auto', color: l.outcome === 'ok' ? 'var(--success)' : l.outcome === 'staff' ? 'var(--info)' : 'var(--danger)' }}>{l.outcome === 'staff' ? 'staff view' : l.outcome}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
